import { createFileRoute } from "@tanstack/react-router";
import { trackPhoneClick } from "@/lib/track-phone-click";
import { trackEmailClick } from "@/lib/track-email-click";
import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import heroImg from "@/assets/lp2-hero.jpg";
import flaecheImg from "@/assets/lp2-tiefgarage-flaeche.jpg";
import markierungenImg from "@/assets/lp2-tiefgarage-markierungen.jpg";
import whgImg from "@/assets/lp2-whg-schutzsystem.jpg";
import teamImg from "@/assets/ib-team.jpg";
import logoImg from "@/assets/logo.png";
import { AttributionFields } from "@/components/AttributionFields";
import { HeroLeadForm } from "@/components/HeroLeadForm";

export const Route = createFileRoute("/tiefgaragenbeschichtung")({
  head: () => ({
    meta: [
      {
        title:
          "Tiefgaragenbeschichtung | Schneider Bodenbeschichtungen GmbH",
      },
      {
        name: "description",
        content:
          "Tiefgaragenbeschichtung für gewerbliche und institutionelle Tiefgaragenobjekte: belastbare Bodenbeschichtung für Stellplätze, Fahrgassen und Rampen. Familiengeführter Fachbetrieb nach WHG mit über 20 Jahren Erfahrung in NRW. Erste Einschätzung anfragen.",
      },
      {
        property: "og:title",
        content:
          "Tiefgaragenbeschichtung – Schneider Bodenbeschichtungen",
      },
      {
        property: "og:description",
        content:
          "Belastbare Bodenbeschichtungen für Tiefgaragen – für Hausverwaltungen, Eigentümer und Betreiber gewerblicher und institutioneller Tiefgaragenobjekte in NRW.",
      },
      { property: "og:type", content: "website" },
      { property: "og:image", content: heroImg },
      { property: "twitter:image", content: heroImg },
      { name: "robots", content: "index,follow" },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://lp.schneider-bodenbeschichtungen.de/tiefgaragenbeschichtung",
      },
    ],
  }),
  component: LandingTiefgarage,
});

const PHONE_TEL = "+492841931010";
const PHONE_DISPLAY = "+49 2841 931010";
const EMAIL = "info@schneider-bbs.de";
const FORM_ENDPOINT = "";

function scrollToForm() {
  document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" });
}

/* ─── Header ─── */
function Header() {
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur-sm">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
        <a href="#hero" className="inline-flex items-center">
          <img
            src={logoImg}
            alt="Schneider Bodenbeschichtungen GmbH"
            className="h-9 w-auto md:h-10"
          />
        </a>
        <a
          href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("tiefgarage_v1", PHONE_TEL)}
          className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:border-foreground"
        >
          <svg viewBox="0 0 20 20" className="h-4 w-4 fill-current" aria-hidden="true">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          <span className="hidden sm:inline">{PHONE_DISPLAY}</span>
          <span className="sm:hidden">Anrufen</span>
        </a>
      </div>
    </header>
  );
}

/* ─── Sticky Desktop CTA bar ─── */
function StickyDesktopCTA() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    function onScroll() {
      setShow(window.scrollY > 600);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      className={`fixed inset-x-0 top-[57px] z-30 hidden border-b border-border bg-background/95 backdrop-blur-sm transition-transform duration-200 md:block ${
        show ? "translate-y-0" : "-translate-y-full"
      }`}
      aria-hidden={!show}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-2.5">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">
            Tiefgaragenbeschichtung – Schneider
          </span>
          <span className="hidden text-border lg:inline">•</span>
          <span className="hidden lg:inline">
            Familiengeführter Fachbetrieb · 20+ Jahre Erfahrung · WHG
          </span>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("tiefgarage_v1", PHONE_TEL)}
            className="hidden h-9 items-center justify-center gap-1.5 rounded-md border border-border px-3 text-sm font-medium text-foreground transition-colors hover:border-foreground lg:inline-flex"
          >
            <svg viewBox="0 0 20 20" className="h-3.5 w-3.5 fill-current" aria-hidden="true">
              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
            </svg>
            {PHONE_DISPLAY}
          </a>
          <button
            type="button"
            onClick={scrollToForm}
            className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Erste Einschätzung anfragen
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Sticky Mobile CTA ─── */
function StickyMobileCTA() {
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 p-2 backdrop-blur-sm md:hidden">
      <div className="flex gap-2">
        <a
          href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("tiefgarage_v1", PHONE_TEL)}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-md border border-foreground px-3 py-2.5 text-sm font-semibold text-foreground"
        >
          Anrufen
        </a>
        <button
          type="button"
          onClick={scrollToForm}
          className="flex flex-1 items-center justify-center rounded-md bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground"
        >
          Einschätzung anfragen
        </button>
      </div>
    </div>
  );
}

/* ─── Section wrapper ─── */
function Section({
  children,
  className = "",
  id,
  gray = false,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
  gray?: boolean;
}) {
  return (
    <section
      id={id}
      className={`scroll-mt-24 ${gray ? "bg-muted/40" : "bg-background"} border-b border-border ${className}`}
    >
      <div className="mx-auto max-w-6xl px-4 py-16 md:py-24">{children}</div>
    </section>
  );
}

/* ─── Star icon ─── */
function Star() {
  return (
    <svg viewBox="0 0 20 20" className="h-4 w-4 fill-current" aria-hidden="true">
      <path d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.79L10 14.77l-5.2 2.74.99-5.79L1.58 7.62l5.82-.85L10 1.5z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 10.5l3.5 3.5 7-8" />
    </svg>
  );
}

/* ─── Hero ─── */
function Hero() {
  return (
    <section
      id="hero"
      className="scroll-mt-24 relative overflow-hidden border-b border-border bg-foreground"
    >
      <img
        src={heroImg}
        alt="Tiefgarage mit hochwertiger Bodenbeschichtung und sauberen Markierungen"
        className="absolute inset-0 h-full w-full object-cover opacity-50"
        fetchPriority="high"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-foreground/80 via-foreground/50 to-transparent" />

      <div className="relative mx-auto max-w-6xl px-4 py-12 md:py-20 lg:py-24">
        <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="min-w-0 lg:col-span-7">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-background/30 bg-background/10 px-3 py-1 text-xs font-medium tracking-wide text-background uppercase backdrop-blur">
            Für Tiefgaragen
          </div>
          <h1 className="text-[1.875rem] font-semibold leading-[1.1] tracking-tight text-background sm:text-4xl md:text-[2.5rem] lg:text-5xl" lang="de">
            Tiefgaragenbeschichtung für Hausverwaltungen, Eigentümer und Betreiber
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-background/90 md:text-lg">
            Belastbare Bodenbeschichtungen für Tiefgaragen – abgestimmt auf
            Untergrund, Nutzung und laufenden Betrieb.
          </p>

          <ul className="mt-6 space-y-2.5">
            <li className="flex items-start gap-2.5 text-sm text-background md:text-base">
              <CheckIcon />
              <span>Für gewerbliche und institutionelle Tiefgaragenobjekte</span>
            </li>
            <li className="flex items-start gap-2.5 text-sm text-background md:text-base">
              <CheckIcon />
              <span>Beschichtung für Stellplätze, Fahrgassen und Rampen in Tiefgaragen</span>
            </li>
            <li className="flex items-start gap-2.5 text-sm text-background md:text-base">
              <CheckIcon />
              <span>Erste Einschätzung in 24 Stunden</span>
            </li>
            <li className="flex items-start gap-2.5 text-sm text-background md:text-base">
              <CheckIcon />
              <span>Keine privaten Einzelgaragen oder DIY-Anfragen</span>
            </li>
          </ul>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={scrollToForm}
              className="inline-flex h-12 items-center justify-center rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-md transition-all hover:bg-primary/90 hover:shadow-lg"
            >
              Erste Einschätzung anfragen
            </button>
            <a
              href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("tiefgarage_v1", PHONE_TEL)}
              className="inline-flex h-12 items-center justify-center rounded-md border border-background/40 bg-background/10 px-6 text-sm font-semibold text-background backdrop-blur transition-colors hover:bg-background/20"
            >
              Lieber anrufen? · {PHONE_DISPLAY}
            </a>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-background/80">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-flex text-primary">
                <Star />
              </span>
              <strong className="text-background">4,6</strong> Google-Bewertung
            </span>
            <span className="hidden text-background/40 md:inline">•</span>
            <span>14 Rezensionen</span>
            <span className="hidden text-background/40 md:inline">•</span>
            <span>Fachbetrieb nach WHG</span>
            <span className="hidden text-background/40 md:inline">•</span>
            <span>Präqualifizierung (PQ)</span>
          </div>

          <div className="mt-5 hidden grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-x-3 sm:gap-y-2 md:grid">
            {[
              "20+ Jahre Erfahrung",
              "Familiengeführter Fachbetrieb",
              "Fachbetrieb nach WHG",
              "Präqualifizierung (PQ)",
            ].map((b) => (
              <span
                key={b}
                className="inline-flex items-center gap-1.5 rounded-md border border-background/30 bg-background/10 px-2.5 py-1.5 text-[11px] font-medium text-background backdrop-blur"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                {b}
              </span>
            ))}
          </div>
          </div>

          <div className="lg:col-span-5">
            <HeroLeadForm
              pageId="tiefgarage_v1"
              formName="Tiefgarage V1 Hero Formular"
              variant="tiefgarage"
              phoneTel={PHONE_TEL}
              phoneDisplay={PHONE_DISPLAY}
              topicOptions={[
                "Tiefgaragenbeschichtung",
                "Bodenbeschichtung in Tiefgaragen",
                "Beschichtung bestehender Tiefgaragenflächen",
                "Stellplätze, Fahrgassen, Rampen",
                "WHG-Bereich",
                "Sonstiges B2B-Projekt",
              ]}
            />
            <p className="mt-4 text-center text-xs leading-relaxed text-background/75 lg:text-left">
              Nicht sicher, ob Ihre Tiefgarage für eine neue Beschichtung
              geeignet ist? Genau dafür ist die erste Einschätzung da.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Trust strip ─── */
function TrustStrip() {
  const items = [
    { label: "4,6 ★ Google-Bewertung", strong: true },
    { label: "14 Google-Rezensionen" },
    { label: "Fachbetrieb nach WHG" },
    { label: "20+ Jahre Erfahrung" },
    { label: "Für gewerbliche & institutionelle Projekte" },
  ];
  return (
    <div className="border-b border-border bg-muted/40">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-4 py-5 text-sm text-muted-foreground md:justify-between">
        {items.map((it) => (
          <span
            key={it.label}
            className={`inline-flex items-center gap-2 ${it.strong ? "font-semibold text-foreground" : ""}`}
          >
            {it.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─── Pain points ─── */
function PainPoints() {
  const items = [
    {
      problem: "Abgenutzte, staubende oder rissige Bodenflächen",
      outcome:
        "Belastbare Beschichtung, die im täglichen Fahr- und Stellverkehr wieder verlässlich nutzbar ist.",
    },
    {
      problem: "Beschichtung bei laufender Nutzung planen",
      outcome:
        "Realistische Bauablaufplanung, die Stellplätze, Zufahrten und Nutzungseinschränkungen einbezieht.",
    },
    {
      problem: "Unklar, welches Beschichtungssystem passt",
      outcome:
        "Klare Einschätzung, welches Beschichtungssystem zu Untergrund, Nutzung und Belastung passt.",
    },
    {
      problem: "Hohe mechanische Belastung durch Fahrverkehr",
      outcome:
        "Beschichtungssysteme, die dem realen Reifen-, Brems- und Parkverkehr in Tiefgaragen standhalten.",
    },
    {
      problem: "Stellplätze, Fahrgassen und Rampen sauber strukturieren",
      outcome:
        "Saubere, eindeutige Markierungen und Verkehrsführung als integraler Bestandteil der fertigen Fläche.",
    },
    {
      problem: "Bestehende Tiefgaragenflächen neu beschichten",
      outcome:
        "Nach Prüfung von Untergrund und Zustand: Neuaufbau einer belastbaren Bodenbeschichtung auf der vorhandenen Fläche.",
    },
  ];
  return (
    <Section id="probleme" gray>
      <div className="mb-10 max-w-3xl">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Typische Probleme im Bestand
        </div>
        <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          Typische Themen in Tiefgaragen
        </h2>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">
          Tiefgaragen sind stark beanspruchte Bodenflächen. Diese Themen begegnen
          uns in Bestandsobjekten von Hausverwaltungen, Eigentümern und
          Betreibern besonders häufig.
        </p>
      </div>
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => (
          <div
            key={it.problem}
            className="flex h-full flex-col rounded-xl border border-border bg-background p-6 shadow-sm"
          >
            <div className="text-[11px] font-semibold uppercase tracking-wider text-primary">
              Problem
            </div>
            <h3 className="mt-1.5 text-base font-semibold tracking-tight text-foreground">
              {it.problem}
            </h3>
            <div className="mt-4 border-t border-border pt-4">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Unsere Antwort
              </div>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                {it.outcome}
              </p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <button
          type="button"
          onClick={scrollToForm}
          className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Erste Einschätzung anfragen
        </button>
        <a
          href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("tiefgarage_v1", PHONE_TEL)}
          className="inline-flex h-11 items-center justify-center rounded-md border border-border bg-background px-6 text-sm font-semibold text-foreground transition-colors hover:border-foreground"
        >
          Lieber anrufen? · {PHONE_DISPLAY}
        </a>
      </div>
    </Section>
  );
}

/* ─── Reviews ─── */
function Reviews() {
  const reviews = [
    {
      text: "Herr Schneider berät ehrlich und nicht nur, um zu verkaufen. Die Beratung war passend zum Objekt und mir wurde eine andere Lösung empfohlen.",
      author: "Hannah Luisa Vimpolsek",
    },
    {
      text: "Einwandfreie und termingerechte Ausführung.",
      author: "Florian Heil",
    },
    {
      text: "Der Service ist einwandfrei und professionell.",
      author: "Sergio B.",
    },
  ];
  return (
    <Section gray>
      <div className="mb-10 max-w-2xl">
        <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          Vertrauen, das sich in echten Bewertungen zeigt
        </h2>
        <p className="mt-3 text-base text-muted-foreground">
          Rückmeldungen aus realen Projekten bestätigen vor allem Beratung,
          Professionalität und termingerechte Ausführung.
        </p>
      </div>
      <div className="grid gap-5 md:grid-cols-3">
        {reviews.map((r) => (
          <figure
            key={r.author}
            className="flex flex-col rounded-xl border border-border bg-background p-6 shadow-sm"
          >
            <div className="mb-3 flex items-center gap-1 text-primary">
              <Star />
              <Star />
              <Star />
              <Star />
              <Star />
            </div>
            <blockquote className="flex-1 text-sm leading-relaxed text-foreground">
              „{r.text}"
            </blockquote>
            <figcaption className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{r.author}</span>
              <span className="block">Google-Rezension</span>
            </figcaption>
          </figure>
        ))}
      </div>
    </Section>
  );
}

/* ─── Reference images ─── */
function ProjectProof() {
  const cards = [
    {
      img: flaecheImg,
      title: "Tiefgarage mit großflächiger Beschichtung",
      text: "Saubere, gleichmäßige und belastbare Flächenlösung für eine intensiv genutzte Tiefgarage – fachgerecht aufgebaut auf den vorhandenen Untergrund.",
      featured: true,
    },
    {
      img: markierungenImg,
      title: "Klare Markierungen und Verkehrsführung",
      text: "Stellplatzlinien, Pfeile und Verkehrsführung gehören zur fertigen Fläche – sauber, eindeutig und auf die Nutzung der Tiefgarage abgestimmt.",
    },
    {
      img: whgImg,
      title: "Schutzsysteme für anspruchsvolle Bereiche",
      text: "Bei technischen Anforderungen kommt es auf die fachgerechte Abstimmung von Untergrund, Schutzaufbau und Nutzung an.",
    },
  ];
  return (
    <Section id="referenzen">
      <div className="mb-10 max-w-2xl">
        <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          Einblicke aus realisierten Tiefgaragenprojekten
        </h2>
        <p className="mt-3 text-base text-muted-foreground">
          Reale Projektbilder zeigen, worauf es in der Praxis ankommt: saubere
          Ausführung, passende Systeme und belastbare Bodenbeschichtungen für
          Tiefgaragen.
        </p>
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        {cards.map((c, i) => (
          <article
            key={c.title}
            className={`overflow-hidden rounded-xl border border-border bg-background shadow-sm ${c.featured ? "md:col-span-2" : ""}`}
          >
            <div className="aspect-[16/10] overflow-hidden bg-muted">
              <img
                src={c.img}
                alt={c.title}
                loading={i === 0 ? "eager" : "lazy"}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="p-6">
              <h3 className="text-lg font-semibold tracking-tight text-foreground">
                {c.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {c.text}
              </p>
            </div>
          </article>
        ))}
      </div>
      <div className="mt-10 flex justify-center">
        <button
          type="button"
          onClick={scrollToForm}
          className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Ähnliches Tiefgaragenprojekt geplant? Anfrage senden
        </button>
      </div>
    </Section>
  );
}

/* ─── Use cases / Leistungen ─── */
function UseCases() {
  const cases = [
    "Tiefgaragenbeschichtung",
    "Bodenbeschichtung in Tiefgaragen",
    "Beschichtung bestehender Tiefgaragenflächen",
    "Stellplätze, Fahrgassen und Rampen",
    "Markierungen & Verkehrsführung",
    "Beschichtungssystem passend zur Nutzung",
  ];
  return (
    <Section id="leistungen" gray>
      <div className="grid gap-10 md:grid-cols-12 md:items-center md:gap-12">
        <div className="order-2 md:order-1 md:col-span-6">
          <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
            Leistungen für Tiefgaragen
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Es kommt nicht auf irgendeine Beschichtung an, sondern auf ein
            Beschichtungssystem, das zum Untergrund, zur Nutzung und zum
            Zustand des Bodens passt. Markierungen und Nutzungslogik sind
            dabei integraler Bestandteil der fertigen Fläche – nicht Beiwerk.
          </p>
          <ul className="mt-6 grid gap-3 sm:grid-cols-2">
            {cases.map((c) => (
              <li
                key={c}
                className="flex items-center gap-2.5 rounded-md border border-border bg-background px-4 py-3 text-sm font-medium text-foreground"
              >
                <CheckIcon />
                {c}
              </li>
            ))}
          </ul>
        </div>
        <div className="order-1 md:order-2 md:col-span-6">
          <div className="overflow-hidden rounded-xl border border-border bg-muted shadow-sm">
            <img
              src={markierungenImg}
              alt="Tiefgarage mit klaren Stellplatzmarkierungen und Verkehrsführung"
              loading="lazy"
              className="aspect-[4/3] h-full w-full object-cover"
            />
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ─── Why Schneider ─── */
function WhySchneider() {
  const cards = [
    {
      title: "Ehrliche Einschätzung statt Maximalangebot",
      text: "Nicht jede Tiefgarage braucht das gleiche System. Entscheidend ist, was zum Objekt, zur Nutzung und zum Zustand des Untergrunds passt.",
    },
    {
      title: "Passende Systemwahl für die reale Nutzung",
      text: "Stellflächen, Fahrgassen, Rampen oder Sonderbereiche – die Beschichtung muss zur tatsächlichen Beanspruchung passen.",
    },
    {
      title: "Planbares Vorgehen im Bestand",
      text: "Bauablauf, Stellplatzverfügbarkeit und Nutzungseinschränkungen werden früh berücksichtigt – nicht erst auf der Baustelle.",
    },
    {
      title: "Saubere Ausführung als Standard",
      text: "Gründliche Untergrundvorbereitung, fachgerechte Verarbeitung und klar dokumentierte Schritte sind die Grundlage belastbarer Flächen.",
    },
  ];
  const proofs = [
    "Fachbetrieb nach WHG",
    "Präqualifizierung (PQ)",
    "Gewerbliche Tiefgaragen",
    "B2B-orientierte Projekte",
  ];
  return (
    <Section id="warum-schneider">
      <div className="mb-10 max-w-2xl">
        <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          Warum Schneider Bodenbeschichtungen
        </h2>
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        {cards.map((c, i) => (
          <div
            key={c.title}
            className="rounded-xl border border-border bg-background p-6 shadow-sm"
          >
            <div className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-sm font-semibold text-primary">
              {String(i + 1).padStart(2, "0")}
            </div>
            <h3 className="text-lg font-semibold tracking-tight text-foreground">
              {c.title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {c.text}
            </p>
          </div>
        ))}
      </div>
      <div className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 border-t border-border pt-6 text-xs text-muted-foreground">
        {proofs.map((p) => (
          <span key={p} className="inline-flex items-center gap-1.5">
            <span className="h-1 w-1 rounded-full bg-primary" />
            {p}
          </span>
        ))}
      </div>
    </Section>
  );
}

/* ─── Authority / Erfahrung ─── */
function Authority() {
  return (
    <Section id="erfahrung-regionalitaet" gray>
      <div className="grid gap-10 md:grid-cols-12 md:items-center md:gap-14">
        <div className="md:col-span-5">
          <div className="overflow-hidden rounded-xl border border-border bg-muted shadow-sm">
            <img
              src={teamImg}
              alt="Maurice Schneider und Andreas Schneider, Geschäftsführung Schneider Bodenbeschichtungen GmbH"
              loading="lazy"
              width={1024}
              height={1024}
              className="aspect-[4/5] h-full w-full object-cover"
            />
          </div>
          <div className="mt-3 text-xs font-medium text-muted-foreground">
            Maurice Schneider &amp; Andreas Schneider
          </div>
        </div>
        <div className="md:col-span-7">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Erfahrung · Kompetenz · Region
          </div>
          <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
            Erfahrung, Kompetenz und Regionalität
          </h2>
          <div className="mt-5 space-y-4 text-base leading-relaxed text-muted-foreground">
            <p>
              Die Schneider Bodenbeschichtungen GmbH ist ein familiengeführter
              Fachbetrieb mit über 20 Jahren Erfahrung im Bereich der
              Bodenbeschichtungen. Unser Firmensitz befindet sich in Moers am
              Niederrhein – von hier aus betreuen wir Projekte in ganz NRW und
              auf Wunsch auch darüber hinaus.
            </p>
            <p>
              Tiefgaragen gehören zu den anspruchsvollsten Einsatzbereichen
              für Bodenbeschichtungen. Mit moderner Technik und praxisnaher
              Erfahrung realisieren wir Projekte wirtschaftlich, zuverlässig
              und nach hohen Qualitätsstandards.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[
              { v: "20+", l: "Jahre Erfahrung" },
              { v: "2", l: "Standorte in NRW" },
              { v: "WHG", l: "Fachbetrieb" },
              { v: "PQ", l: "Präqualifiziert" },
            ].map((s) => (
              <div
                key={s.l}
                className="rounded-lg border border-border bg-background p-4"
              >
                <div className="text-xl font-semibold text-foreground">
                  {s.v}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {s.l}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-lg border border-border bg-background p-6">
            <h3 className="text-base font-semibold tracking-tight text-foreground">
              Regional stark aufgestellt in Nordrhein-Westfalen
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Mit Standorten in Moers und Wuppertal betreuen wir
              Tiefgaragenprojekte in ganz Nordrhein-Westfalen – vom
              Niederrhein über das Ruhrgebiet und das Bergische Land bis in
              den Großraum Köln.
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-md border border-border bg-muted/40 p-3 text-xs">
                <div className="font-semibold text-foreground">Moers</div>
                <div className="mt-0.5 text-muted-foreground">
                  Eurotec-Ring 36 · 47445 Moers
                </div>
              </div>
              <div className="rounded-md border border-border bg-muted/40 p-3 text-xs">
                <div className="font-semibold text-foreground">Wuppertal</div>
                <div className="mt-0.5 text-muted-foreground">
                  Kreuzstraße 20 · 42277 Wuppertal
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ─── Technical credibility ─── */
function Technical() {
  const blocks = [
    {
      title: "Bestehende Tiefgaragenflächen neu beschichten",
      text: "Viele Projekte starten nicht auf einem Neubau, sondern auf vorhandenen, bereits beanspruchten Bodenflächen. Nach Prüfung von Untergrund und Zustand lässt sich ein technisch und wirtschaftlich passendes Beschichtungssystem abstimmen.",
    },
    {
      title: "Beschichtungssystem zur Nutzung wählen",
      text: "Je nach Objekt unterscheiden sich Beschichtungsaufbauten in Stellplätzen, Fahrgassen oder Rampen – immer abgestimmt auf Untergrund, Nutzung und Belastung.",
    },
    {
      title: "WHG- und Sonderanforderungen berücksichtigen",
      text: "Wo es technisch notwendig ist, kommen Aufbauten nach WHG zum Einsatz – als Teil einer fachlich abgestimmten Gesamtbeschichtung.",
    },
  ];
  return (
    <Section id="technik">
      <div className="mb-10 max-w-3xl">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Technik · Beschichtung · Bestand
        </div>
        <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          Tiefgaragen fachgerecht neu beschichten
        </h2>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {blocks.map((b) => (
          <div
            key={b.title}
            className="rounded-xl border border-border bg-background p-6 shadow-sm"
          >
            <h3 className="text-base font-semibold tracking-tight text-foreground">
              {b.title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {b.text}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-10 rounded-xl border border-border bg-muted/40 p-6 md:p-8">
        <h3 className="text-xl font-semibold tracking-tight text-foreground md:text-2xl">
          Fachbetrieb nach WHG &amp; präqualifizierter Partner
        </h3>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground md:text-base">
          Schneider ist als Fachbetrieb nach WHG zertifiziert und verfügt über
          die erforderliche Präqualifizierung für Bauvorhaben mit besonderen
          Anforderungen – ein Trust-Signal, das im Tiefgaragenumfeld für
          Verlässlichkeit steht.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          {[
            "Fachbetrieb nach WHG",
            "Präqualifizierung (PQ)",
            "Beschichtungssysteme",
            "Beschichtung im Bestand",
          ].map((b) => (
            <span
              key={b}
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              {b}
            </span>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ─── Reference proof strip / mini cases ─── */
function ReferenceCases() {
  const refs = [
    {
      ort: "Beckum",
      title: "Tiefgarage mit Remmers OS 8 und weißen Markierungen",
      text: "Belastbares Beschichtungssystem für eine Tiefgarage – kombiniert mit klaren weißen Markierungen für Stellplätze und Verkehrsführung.",
    },
    {
      ort: "St. Augustin / Eitorf",
      title: "Parallele Tiefgaragenprojekte mit Remmers OS 8",
      text: "Zwei parallel betreute Tiefgaragenprojekte mit dem gleichen Systemansatz – fachgerecht geplant, sauber umgesetzt.",
    },
    {
      ort: "Datteln",
      title: "Halle mit ableitfähiger KLB WHG-Beschichtung",
      text: "Neue Bodenbeschichtung mit gründlicher Untergrundvorbereitung und anschließender ableitfähiger WHG-Beschichtung im technisch sensiblen Umfeld.",
    },
    {
      ort: "Moers",
      title: "Kontaminierte Grube mit Remmers FAS Spezialgrundierung",
      text: "Spezialgrundierung in einer kontaminierten Grube, gefolgt von einer fachgerechten WHG-Beschichtung.",
    },
  ];
  return (
    <Section gray>
      <div className="mb-10 max-w-2xl">
        <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          Konkrete Projekte aus der Praxis
        </h2>
        <p className="mt-3 text-base text-muted-foreground">
          Eine Auswahl realisierter Tiefgaragenprojekte – stellvertretend für
          unseren Anspruch an Systemwahl und Ausführung.
        </p>
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        {refs.map((r) => (
          <div
            key={r.title}
            className="rounded-xl border border-border bg-background p-6 shadow-sm"
          >
            <div className="text-[11px] font-semibold uppercase tracking-wider text-primary">
              {r.ort}
            </div>
            <h3 className="mt-1.5 text-base font-semibold tracking-tight text-foreground">
              {r.title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              {r.text}
            </p>
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ─── FAQ ─── */
function FAQ() {
  const items = [
    {
      q: "Wann ist eine Tiefgaragenbeschichtung sinnvoll?",
      a: "Sinnvoll ist sie typischerweise dann, wenn die vorhandene Bodenfläche stark beansprucht, abgenutzt, rissig oder schwer zu reinigen ist – oder wenn der Boden im laufenden Betrieb wieder verlässlich nutzbar werden soll.",
    },
    {
      q: "Können bestehende Tiefgaragenflächen neu beschichtet werden?",
      a: "Ja, wenn Untergrund, Nutzung und Zustand dafür geeignet sind. Genau das wird in der ersten Einschätzung geprüft.",
    },
    {
      q: "Kann im laufenden Betrieb gearbeitet werden?",
      a: "Häufig ja – etwa abschnittsweise oder in abgestimmten Zeitfenstern. Die Bauablaufplanung berücksichtigt Stellplatzverfügbarkeit, Zufahrten und Nutzungseinschränkungen frühzeitig.",
    },
    {
      q: "Wie läuft die Einschätzung vor Ort ab?",
      a: "Nach der Anfrage werden die wesentlichen Eckdaten geprüft. Bei passenden Projekten folgt eine Einschätzung von Untergrund, Belastung und sinnvoller Vorgehensweise – als Grundlage für ein belastbares Angebot.",
    },
    {
      q: "Für welche Tiefgaragen ist das geeignet?",
      a: "Für Tiefgaragen von Wohn- und Gewerbeimmobilien, gewerbliche Stellflächen und vergleichbare Tiefgaragenobjekte mit institutionellem oder gewerblichem Hintergrund. Private Einzelgaragen gehören nicht zum Leistungsumfang.",
    },
    {
      q: "Welche Markierungen sind möglich?",
      a: "Stellplatzlinien, Verkehrsführung, Pfeile und Sonderbereiche werden als integraler Bestandteil der fertigen Fläche geplant – passend zur Nutzungslogik des Objekts.",
    },
  ];
  return (
    <Section id="faq">
      <div className="mb-10 max-w-2xl">
        <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
          Häufige Fragen zur Tiefgaragenbeschichtung
        </h2>
      </div>
      <Accordion type="single" collapsible className="mx-auto max-w-3xl">
        {items.map((it, i) => (
          <AccordionItem
            key={i}
            value={`item-${i}`}
            className="border-b border-border"
          >
            <AccordionTrigger className="py-5 text-left text-base font-semibold text-foreground hover:no-underline">
              {it.q}
            </AccordionTrigger>
            <AccordionContent className="pb-5 text-sm leading-relaxed text-muted-foreground">
              {it.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Section>
  );
}

/* ─── Final CTA ─── */
function FinalCTA() {
  return (
    <Section id="projektanfrage" gray>
      <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-foreground px-8 py-12 text-center text-background md:px-12 md:py-16">
        <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">
          Tiefgaragenbeschichtung prüfen lassen
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-background/75">
          Unsicher, ob Ihre Tiefgarage für eine neue Beschichtung geeignet ist?
          Senden Sie uns die wichtigsten Eckdaten – wir prüfen Ihr Projekt und
          melden uns in der Regel innerhalb von 24 Stunden.
        </p>
        <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <button
            type="button"
            onClick={scrollToForm}
            className="inline-flex h-12 items-center justify-center rounded-md bg-primary px-7 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Erste Einschätzung anfragen
          </button>
          <a
            href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("tiefgarage_v1", PHONE_TEL)}
            className="inline-flex h-12 items-center justify-center rounded-md border border-background/30 px-7 text-sm font-semibold text-background transition-colors hover:bg-background/10"
          >
            Lieber anrufen?
          </a>
        </div>
      </div>
    </Section>
  );
}

/* ─── Contact form ─── */
function ContactForm() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    e.stopPropagation();
    setStatus("sending");
    const formEl = e.currentTarget;
    const fd = new FormData(formEl);
    const payload = Object.fromEntries(fd.entries()) as Record<string, string>;
    try {
      const { submitLead } = await import("@/lib/submit-lead");
      await submitLead(
        {
          name: payload.name || "",
          firma: payload.firma || "",
          email: payload.email || "",
          telefon: payload.telefon || "",
          ort: payload.ort || "",
          betreff: payload.betreff || "",
          nachricht: payload.nachricht || "",
        },
        "tiefgarage_v1",
        payload.website || "",
        formEl
      );
      if (typeof window !== "undefined") {
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({
          event: "generate_lead",
          page_id: "tiefgarage_v1",
        });
      }
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  const inputClass =
    "w-full rounded-md border border-border bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-foreground focus:outline-none focus:ring-1 focus:ring-foreground";
  const labelClass = "mb-1.5 block text-xs font-medium text-foreground";

  return (
    <Section id="kontakt">
      <div className="grid gap-10 md:grid-cols-12 md:gap-12">
        <div className="md:col-span-5">
          <h2 className="text-3xl font-semibold tracking-tight text-foreground md:text-4xl">
            Erste Einschätzung für Ihre Tiefgaragenbeschichtung anfragen
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Senden Sie uns die wichtigsten Eckdaten zu Objekt, Fläche und
            Zustand des Bodens. Wir prüfen, ob Ihr Projekt zu Schneider passt,
            und melden uns in der Regel innerhalb von 24 Stunden.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {[
              "Unverbindlich",
              "Nur für gewerbliche Projekte",
              "Antwort in 24 Stunden",
            ].map((p) => (
              <span
                key={p}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-foreground"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                {p}
              </span>
            ))}
          </div>
          <p className="mt-5 rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm leading-relaxed text-foreground">
            Nicht sicher, ob Ihre Tiefgarage für eine neue Beschichtung
            geeignet ist? Genau dafür ist die erste Einschätzung da.
          </p>
          <div className="mt-8 space-y-5 text-sm text-foreground">
            <div className="rounded-lg border border-border bg-muted/30 p-5">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Sie möchten lieber direkt sprechen?
              </div>
              <a
                href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("tiefgarage_v1", PHONE_TEL)}
                className="mt-1.5 inline-flex items-center gap-2 text-xl font-semibold tracking-tight text-foreground hover:text-primary"
              >
                <svg viewBox="0 0 20 20" className="h-5 w-5 fill-current text-primary" aria-hidden="true">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                {PHONE_DISPLAY}
              </a>
              <p className="mt-2 text-xs text-muted-foreground">
                Rufen Sie uns an – Alternative zum Formular für gewerbliche
                und institutionelle Projekte.
              </p>
            </div>

            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Sie möchten Unterlagen per E-Mail senden?
              </div>
              <a
                href={`mailto:${EMAIL}`}
                onClick={() => trackEmailClick("tiefgarage_v1", EMAIL)}
                className="mt-1 inline-block text-sm text-foreground underline-offset-4 hover:text-primary hover:underline"
              >
                Schreiben Sie an: {EMAIL}
              </a>
            </div>

            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Anschrift
              </div>
              <div className="mt-1 text-sm text-foreground">
                Schneider Bodenbeschichtungen GmbH
                <br />
                Eurotec-Ring 36
                <br />
                47445 Moers
              </div>
            </div>

            <ul className="space-y-2 border-t border-border pt-5 text-xs text-muted-foreground">
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>Ausschließlich für gewerbliche und institutionelle Anfragen</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>Persönliche Einschätzung für Ihr Objekt</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>Fachbetrieb nach WHG · Präqualifizierung (PQ)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckIcon />
                <span>Familiengeführter Fachbetrieb mit über 20 Jahren Erfahrung</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="md:col-span-7">
          {status === "success" ? (
            <div
              role="status"
              aria-live="polite"
              className="rounded-2xl border-2 border-primary bg-background p-6 shadow-2xl shadow-primary/30 md:p-8"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <svg viewBox="0 0 24 24" className="h-6 w-6 fill-none stroke-current" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12l5 5L20 7" />
                </svg>
              </div>
              <h3 className="mt-5 text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
                Vielen Dank für Ihre Anfrage
              </h3>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                Wir haben Ihre Anfrage erhalten. Wir prüfen Ihre Angaben und
                melden uns in der Regel innerhalb von 24 Stunden mit einer
                ersten Einschätzung.
              </p>
              <p className="mt-4 text-sm text-foreground">
                Sie erreichen uns auch direkt unter{" "}
                <a
                  href={`tel:${PHONE_TEL}`}
                  onClick={() => trackPhoneClick("tiefgarage_v1", PHONE_TEL)}
                  className="font-semibold text-primary hover:underline"
                >
                  {PHONE_DISPLAY}
                </a>
                .
              </p>
            </div>
          ) : (
          <div className="relative">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -inset-2 rounded-2xl bg-primary/10 blur-2xl"
            />
            <form
              onSubmit={onSubmit}
              id="tiefgarage_v1_kontakt"
              data-gj-no-native-submit="1"
              data-form-id="tiefgarage_v1_kontakt"
              data-form-name="Tiefgarage V1 Abschluss-Formular"
              className="relative rounded-2xl border-2 border-primary bg-background p-6 shadow-2xl shadow-primary/30 md:p-8"
            >
              <AttributionFields />
              <div className="-mt-10 mb-5 flex justify-center md:justify-start">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary-foreground shadow-md">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
                  Kostenlose Erstberatung
                </span>
              </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className={labelClass}>
                  Ihr Name *
                </label>
                <input id="name" name="name" required className={inputClass} />
              </div>
              <div>
                <label htmlFor="firma" className={labelClass}>
                  Firma / Objekt *
                </label>
                <input id="firma" name="firma" required className={inputClass} />
              </div>
              <div>
                <label htmlFor="email" className={labelClass}>
                  Ihre E-Mail-Adresse *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="telefon" className={labelClass}>
                  Telefonnummer *
                </label>
                <input
                  id="telefon"
                  name="telefon"
                  type="tel"
                  required
                  className={inputClass}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="ort" className={labelClass}>
                  Ort / Objektstandort
                </label>
                <input id="ort" name="ort" className={inputClass} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="betreff" className={labelClass}>
                  Worum geht es? *
                </label>
                <select
                  id="betreff"
                  name="betreff"
                  required
                  defaultValue=""
                  className={inputClass}
                >
                  <option value="" disabled>
                    Bitte wählen
                  </option>
                  <option>Tiefgaragenbeschichtung</option>
                  <option>Bodenbeschichtung in Tiefgaragen</option>
                  <option>Beschichtung bestehender Tiefgaragenflächen</option>
                  <option>Stellplätze, Fahrgassen, Rampen</option>
                  <option>WHG-Bereich / Sonderanforderung</option>
                  <option>Sonstiges Objekt</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="nachricht" className={labelClass}>
                  Kurze Beschreibung *
                </label>
                <textarea
                  id="nachricht"
                  name="nachricht"
                  required
                  rows={5}
                  placeholder="z. B. Tiefgarage in Köln, ca. 80 Stellplätze, Boden staubt, ist abgenutzt oder soll neu beschichtet werden."
                  className={inputClass}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={status === "sending"}
              className="mt-6 inline-flex h-12 w-full items-center justify-center rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-md transition-all hover:bg-primary/90 hover:shadow-lg disabled:opacity-60"
            >
              {status === "sending"
                ? "Wird gesendet…"
                : "Erste Einschätzung anfragen"}
            </button>
            <p className="mt-3 text-center text-[11px] font-medium text-primary">
              Unverbindlich · Nur für gewerbliche Projekte · Antwort in 24 Stunden
            </p>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
              Nach dem Absenden prüfen wir Ihre Angaben. Wenn das Projekt passt,
              melden wir uns zur ersten Einschätzung oder zur Abstimmung der
              nächsten Schritte.
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              Pflichtfelder mit *. Nur für gewerbliche und institutionelle Anfragen.
            </p>

            {status === "error" && (
              <p className="mt-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                Beim Senden ist etwas schiefgegangen. Bitte versuchen Sie es
                erneut oder rufen Sie uns an.
              </p>
            )}
            </form>
          </div>
          )}
        </div>
      </div>
    </Section>
  );
}

/* ─── Footer ─── */
function Footer() {
  return (
    <footer className="bg-background pb-24 pt-10 md:pb-10">
      <div className="mx-auto max-w-6xl px-4">
        <div className="grid gap-6 border-t border-border pt-8 md:grid-cols-12 md:gap-8">
          <div className="md:col-span-5 text-xs text-muted-foreground">
            <div className="font-semibold text-foreground">
              Schneider Bodenbeschichtungen GmbH
            </div>
            <div className="mt-1">Eurotec-Ring 36 · 47445 Moers</div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              <a href={`tel:${PHONE_TEL}`} onClick={() => trackPhoneClick("tiefgarage_v1", PHONE_TEL)} className="hover:text-foreground">
                {PHONE_DISPLAY}
              </a>
              <a href={`mailto:${EMAIL}`} onClick={() => trackEmailClick("tiefgarage_v1", EMAIL)} className="hover:text-foreground">
                {EMAIL}
              </a>
            </div>
          </div>
          <div className="md:col-span-4 text-xs text-muted-foreground">
            <div className="font-semibold text-foreground">Standort Wuppertal</div>
            <div className="mt-1">Kreuzstraße 20 · 42277 Wuppertal</div>
          </div>
          <nav className="md:col-span-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground/80 md:justify-end">
            <a
              href="https://schneider-bodenbeschichtungen.de/impressum/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground"
            >
              Impressum
            </a>
            <a
              href="https://schneider-bodenbeschichtungen.de/datenschutzerklaerung/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground"
            >
              Datenschutz
            </a>
            <a
              href="https://schneider-bodenbeschichtungen.de/agb/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-foreground"
            >
              AGB
            </a>
          </nav>
        </div>
        <div className="mt-6 text-xs text-muted-foreground">
          © {new Date().getFullYear()} Schneider Bodenbeschichtungen GmbH
        </div>
      </div>
    </footer>
  );
}

/* ─── Page ─── */
function LandingTiefgarage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <StickyDesktopCTA />
      <main>
        <Hero />
        <TrustStrip />
        <PainPoints />
        <Reviews />
        <ProjectProof />
        <UseCases />
        <WhySchneider />
        <Authority />
        <Technical />
        <ReferenceCases />
        <FAQ />
        <FinalCTA />
        <ContactForm />
      </main>
      <Footer />
      <StickyMobileCTA />
    </div>
  );
}
